import type { ImportFieldNode } from '@erp/rpc';
import {
  SQL_SCHEMA_FIELD_TYPES,
  type ExtensionTargetModel,
} from '@erp/shared-types';
import {
  findRowInTree,
  normalizeRootFieldKey,
  rowsMissingReferencedEntity,
  rowsToImportNodes,
  rowsWithInvalidDefault,
  rowsWithInvalidNumericArgs,
  rowsWithInvalidSqlDefaultValue,
  type FriendlyFieldRow,
} from './quickSchemaFieldTree';

/**
 * The save path of the New schema dialog, without React: what blocks a save,
 * what gets created, and the RPC sequence that creates it.
 */

/** Why a save can't start. The dialog maps each one to a toast. */
export type QuickSchemaIssue =
  | 'missing_extension_target'
  | 'missing_name'
  | 'not_a_column'
  | 'invalid_default'
  | 'invalid_numeric'
  | 'missing_referenced_entity'
  | 'invalid_sql_default'
  | 'no_fields';

export interface QuickSchemaSaveInput {
  quickType: 'extraction' | 'extension' | 'sql';
  displayName: string;
  extensionTarget: ExtensionTargetModel | null;
  /** The name an extension gets: an entity has one, named after it. */
  extensionName: string;
  /** The call site's target model, for non-extension schemas. */
  defaultTargetModel: string | undefined;
  rows: FriendlyFieldRow[];
  keyRowIds: string[];
}

export interface QuickSchemaSavePlan {
  name: string;
  slug: string;
  targetModel: string;
  schemaType: 'extraction' | 'sql';
  /** The entity to register the schema against once published. */
  registerAs: ExtensionTargetModel | null;
  nodes: ImportFieldNode[];
  rows: FriendlyFieldRow[];
  keyRowIds: string[];
}

export type QuickSchemaSavePlanResult =
  | { ok: true; plan: QuickSchemaSavePlan }
  | { ok: false; issue: QuickSchemaIssue };

const SQL_FIELD_TYPES: ReadonlySet<string> = new Set(SQL_SCHEMA_FIELD_TYPES);

/**
 * A `sql` schema's fields are flat columns. Rows built under Extraction can
 * still hold objects or arrays after the type is switched; catch them before
 * anything is created.
 */
function hasNonColumnRow(rows: readonly FriendlyFieldRow[]): boolean {
  return rows.some(
    (row) => !SQL_FIELD_TYPES.has(row.fieldType) || row.children.length > 0,
  );
}

/** Checked in order; the first failing one is reported. */
function firstIssue(
  input: QuickSchemaSaveInput,
  name: string,
): QuickSchemaIssue | null {
  const checks: readonly [QuickSchemaIssue, () => boolean][] = [
    [
      'missing_extension_target',
      () => input.quickType === 'extension' && !input.extensionTarget,
    ],
    ['missing_name', () => !name],
    [
      'not_a_column',
      () => input.quickType !== 'extraction' && hasNonColumnRow(input.rows),
    ],
    ['invalid_default', () => rowsWithInvalidDefault(input.rows).length > 0],
    [
      'invalid_numeric',
      () => rowsWithInvalidNumericArgs(input.rows).length > 0,
    ],
    [
      'missing_referenced_entity',
      () => rowsMissingReferencedEntity(input.rows).length > 0,
    ],
    [
      'invalid_sql_default',
      () => rowsWithInvalidSqlDefaultValue(input.rows).length > 0,
    ],
    ['no_fields', () => rowsToImportNodes(input.rows).length < 1],
  ];
  return checks.find(([, failed]) => failed())?.[0] ?? null;
}

export function planQuickSchemaSave(
  input: QuickSchemaSaveInput,
): QuickSchemaSavePlanResult {
  const registerAs =
    input.quickType === 'extension' ? input.extensionTarget : null;
  const name = registerAs ? input.extensionName : input.displayName.trim();
  const issue = firstIssue(input, name);
  if (issue) return { ok: false, issue };

  return {
    ok: true,
    plan: {
      name,
      slug: `quick-schema-${crypto.randomUUID().replace(/-/g, '')}`,
      targetModel:
        registerAs ?? (input.defaultTargetModel?.trim() || 'CUSTOM_EXTRACTION'),
      // An entity extension is a `sql` schema under the hood.
      schemaType: input.quickType === 'extraction' ? 'extraction' : 'sql',
      registerAs,
      nodes: rowsToImportNodes(input.rows),
      rows: input.rows,
      // An extension table is keyed by its entity, so it takes no key fields.
      keyRowIds: registerAs ? [] : input.keyRowIds,
    },
  };
}

/**
 * Maps staged key rows (in order) to created field ids. Rows that didn't make
 * it into the schema (e.g. unnamed) resolve to no key and are dropped.
 */
export function keyFieldIdsFor(
  keyRowIds: readonly string[],
  rows: FriendlyFieldRow[],
  createdFields: readonly { id: string; fieldName: string }[],
): string[] {
  const nameToId = new Map(createdFields.map((f) => [f.fieldName, f.id]));
  const ids: string[] = [];
  for (const rowId of keyRowIds) {
    const row = findRowInTree(rows, rowId);
    const id = nameToId.get(normalizeRootFieldKey(row?.fieldName ?? ''));
    if (id !== undefined) ids.push(id);
  }
  return ids;
}

/** The RPCs a save needs, so the sequence can be tested without a client. */
export interface QuickSchemaSaveApi {
  create(input: {
    name: string;
    slug: string;
    targetModel: string;
    schemaType: 'extraction' | 'sql';
    mode?: 'standalone';
    baseSchemaRef: null;
    documentType: null;
  }): Promise<{ id: string }>;
  importFields(input: {
    schemaDefinitionId: string;
    fields: ImportFieldNode[];
  }): Promise<{ schema: { fields: { id: string; fieldName: string }[] } }>;
  update(input: { id: string; keyedBy: string[] }): Promise<unknown>;
  publishVersion(input: { schemaDefinitionId: string }): Promise<{
    id: string;
  }>;
  register(input: {
    schemaDefinitionId: string;
    targetModel: ExtensionTargetModel;
  }): Promise<unknown>;
  delete(input: { id: string }): Promise<unknown>;
}

/**
 * Creates, fills, keys, publishes and (for an extension) registers the schema.
 * Any failure after the create deletes the half-built schema, best effort, and
 * rethrows.
 */
export async function saveQuickSchema(
  plan: QuickSchemaSavePlan,
  api: QuickSchemaSaveApi,
): Promise<{ schemaDefinitionId: string; publishedVersionId: string }> {
  const schema = await api.create({
    name: plan.name,
    slug: plan.slug,
    targetModel: plan.targetModel,
    schemaType: plan.schemaType,
    // `mode` is an extraction concept; `sql` schemas don't use it.
    mode: plan.schemaType === 'extraction' ? 'standalone' : undefined,
    baseSchemaRef: null,
    documentType: null,
  });

  try {
    const imported = await api.importFields({
      schemaDefinitionId: schema.id,
      fields: plan.nodes,
    });
    const keyedBy = keyFieldIdsFor(
      plan.keyRowIds,
      plan.rows,
      imported.schema.fields,
    );
    if (keyedBy.length > 0) await api.update({ id: schema.id, keyedBy });

    const version = await api.publishVersion({ schemaDefinitionId: schema.id });
    if (plan.registerAs) {
      await api.register({
        schemaDefinitionId: schema.id,
        targetModel: plan.registerAs,
      });
    }
    return { schemaDefinitionId: schema.id, publishedVersionId: version.id };
  } catch (error) {
    await api.delete({ id: schema.id }).catch(() => undefined);
    throw error;
  }
}
